import React, { useState, useEffect } from 'react';
import AuthLoginForm from './components/AuthLoginForm.js';
import LayoutSidebar from './components/LayoutSidebar.js';
import OfficeCreateForm from './components/OfficeCreateForm.js';
import OfficeListDisplay from './components/OfficeListDisplay.js';
import UserManagePanel from './components/UserManagePanel.js';

const API_URL = process.env.REACT_APP_API_URL; 

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('createOffice');
  const [users, setUsers] = useState([]); 
  const [offices, setOffices] = useState([]); 
  const [areas, setAreas] = useState([]); 

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('currentUser'));
    if (storedUser) {
      setCurrentUser(storedUser);
      if (storedUser.role === 'ADMIN' || storedUser.role === 'VISOR') {
        fetchUsers(storedUser.access_token, storedUser.role);
        fetchAreas(storedUser.access_token);
      }
      fetchOffices(storedUser.access_token, storedUser.role, storedUser.id);
    }
  }, []);

  const fetchUsers = async (token, role) => {
    if (role !== 'ADMIN' && role !== 'VISOR') return;
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al obtener usuarios.');
      }
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      alert(error.message);
    }
  };

  const fetchOffices = async (token, role, userId) => {
    try {
      const response = await fetch(`${API_URL}/offices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al obtener oficios.');
      }
      const data = await response.json();
      setOffices(data);
    } catch (error) {
      alert(error.message);
    }
  };

  const fetchAreas = async (token) => {
    try {
      const response = await fetch(`${API_URL}/areas`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al obtener áreas.');
      }
      const data = await response.json();
      setAreas(data);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleLogin = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error de autenticación.');
      }
      const data = await response.json();
      const loggedInUser = { ...data.user, access_token: data.access_token };
      setCurrentUser(loggedInUser);
      localStorage.setItem('currentUser', JSON.stringify(loggedInUser));
      setCurrentPage('createOffice'); 
      
      if (loggedInUser.role === 'ADMIN' || loggedInUser.role === 'VISOR') {
        fetchUsers(loggedInUser.access_token, loggedInUser.role);
        fetchAreas(loggedInUser.access_token);
      }
      fetchOffices(loggedInUser.access_token, loggedInUser.role, loggedInUser.id); 
    } catch (error) {
      alert(error.message);
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    setUsers([]);
    setOffices([]);
    setAreas([]); 
    setCurrentPage('createOffice'); 
  };

  const handleCreateOffice = async (officeData, originalFile) => {
    if (!currentUser) {
      alert('Debes iniciar sesión para crear un oficio.');
      return null;
    }
    if (!currentUser.isActive) {
      alert('Tu cuenta está inhabilitada y no puede crear oficios. Contacta al administrador.');
      return null;
    }

    const formData = new FormData();
    formData.append('name', officeData.name);
    formData.append('justification', officeData.justification);
    formData.append('originalFile', originalFile);
    formData.append('areaSigla', officeData.areaSigla); 

    try {
      const response = await fetch(`${API_URL}/offices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.access_token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear oficio.');
      }

      const data = await response.json();
      alert(`Oficio creado con folio: ${data.folio}`);
      fetchOffices(currentUser.access_token, currentUser.role, currentUser.id); 
      return data.folio;
    } catch (error) {
      alert(error.message);
      return null;
    }
  };

  const handleUploadSignedFile = async (officeId, signedFile) => {
    if (!currentUser) {
      alert('Debes iniciar sesión para subir un archivo.');
      return;
    }
    if (!currentUser.isActive) {
      alert('Tu cuenta está inhabilitada y no puede subir archivos firmados. Contacta al administrador.');
      return;
    }

    const formData = new FormData();
    formData.append('signedFile', signedFile);

    try {
      const response = await fetch(`${API_URL}/offices/upload-signed/${officeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.access_token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al subir archivo firmado.');
      }
      alert('Archivo firmado subido exitosamente.');
      fetchOffices(currentUser.access_token, currentUser.role, currentUser.id); 
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreateUser = async (username, password, role, email, areaSigla) => { 
    if (!currentUser || currentUser.role !== 'ADMIN') {
      alert('No tienes permiso para crear usuarios.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.access_token}`
        },
        body: JSON.stringify({ username, password, role, email, areaSigla }) 
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear usuario.');
      }
      alert(`Usuario ${username} creado con rol ${role}.`);
      fetchUsers(currentUser.access_token, currentUser.role); 
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUpdateUser = async (userId, updatedUserData) => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      alert('No tienes permiso para actualizar usuarios.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'PUT', 
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.access_token}`
        },
        body: JSON.stringify(updatedUserData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar usuario.');
      }
      alert(`Usuario ${updatedUserData.username} actualizado exitosamente.`);
      fetchUsers(currentUser.access_token, currentUser.role); 
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSetUserActiveStatus = async (userId, newStatus) => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      alert('No tienes permiso para cambiar el estado de los usuarios.');
      return;
    }
    const action = newStatus ? 'habilitar' : 'inhabilitar';
    if (!window.confirm(`¿Estás seguro de que quieres ${action} este usuario?`)) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'PUT', 
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.access_token}`
        },
        body: JSON.stringify({ isActive: newStatus }) 
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error al ${action} usuario.`);
      }
      alert(`Usuario ${action} exitosamente.`);
      fetchUsers(currentUser.access_token, currentUser.role); 
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreateArea = async (areaData) => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      alert('No tienes permiso para crear áreas.');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/areas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.access_token}`
        },
        body: JSON.stringify(areaData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear área.');
      }
      alert(`Área ${areaData.name} creada exitosamente.`);
      fetchAreas(currentUser.access_token); 
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUpdateArea = async (areaId, updatedAreaData) => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      alert('No tienes permiso para actualizar áreas.');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/areas/${areaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.access_token}`
        },
        body: JSON.stringify(updatedAreaData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar área.');
      }
      alert(`Área ${updatedAreaData.name} actualizada exitosamente.`);
      fetchAreas(currentUser.access_token); 
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteArea = async (areaId) => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      alert('No tienes permiso para eliminar áreas.');
      return;
    }
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta área? Ten en cuenta que los oficios asociados a esta área tendrán su campo de área en NULL.')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/areas/${areaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.access_token}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar área.');
      }
      alert('Área eliminada exitosamente.');
      fetchAreas(currentUser.access_token); 
    } catch (error) {
      alert(error.message);
    }
  };


  const handleNavigation = (page) => {
    if (page === 'logout') {
      handleLogout(); 
    } else {
      setCurrentPage(page); 
    }
  };

  useEffect(() => {
    if (currentUser) {
      if (currentPage === 'listOffices' || currentPage === 'createOffice') {
        fetchOffices(currentUser.access_token, currentUser.role, currentUser.id);
      }
      if (currentPage === 'manageUsers' && (currentUser.role === 'ADMIN' || currentUser.role === 'VISOR')) {
        fetchUsers(currentUser.access_token, currentUser.role);
        fetchAreas(currentUser.access_token); 
      }
    }
  }, [currentPage, currentUser]); 

  if (!currentUser) {
    return <AuthLoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-800 to-indigo-900"> 
      <LayoutSidebar userRole={currentUser.role} onNavigate={handleNavigation} currentPage={currentPage} />
      <main className="flex-1 p-8 overflow-auto">
        {currentPage === 'createOffice' && (
          <OfficeCreateForm onCreateOffice={handleCreateOffice} userAreaSigla={currentUser.areaSigla} />
        )}
        {currentPage === 'listOffices' && (
          <OfficeListDisplay
            offices={offices}
            userRole={currentUser.role}
            currentUserId={currentUser.id}
            currentUserAreaSigla={currentUser.areaSigla} 
            onUploadSignedFile={handleUploadSignedFile}
            allUsers={users} 
            allAreas={areas} 
          />
        )}
        {currentPage === 'manageUsers' && currentUser.role === 'ADMIN' && (
          <UserManagePanel 
            users={users} 
            onCreateUser={handleCreateUser} 
            onUpdateUser={handleUpdateUser} 
            onSetUserActiveStatus={handleSetUserActiveStatus} 
            areas={areas} 
            onCreateArea={handleCreateArea} 
            onUpdateArea={handleUpdateArea}
            onDeleteArea={handleDeleteArea}
          />
        )}
      </main>
    </div>
  );
};

export default App;
